import { BUILD_BRANCH, BUILD_COMMIT, IS_PREVIEW } from '@/buildInfo';

/*
 * The writes-are-real warning shown on every per-branch preview build.
 *
 * 🔴 **WHY THIS EXISTS: A PREVIEW CALLS THE PRODUCTION API.** The owner's
 * decision, 8 September 2026, taken with a separate staging backend offered as
 * the alternative and chosen against on cost. So a preview is not a sandbox in
 * any sense — it is unreviewed code pointed at the live database and the live
 * integrations. On this system that means a save can push a real document into
 * the accountant's Xero inline, an envelope can email a real customer for a
 * legally binding signature, and a delete cascades against real rows.
 *
 * Nothing about the app LOOKS different on a preview. The URL carries a `/b/`
 * segment that nobody reads, and every screen, every record and every button is
 * the production one. This bar is the only thing standing between "I am trying
 * a branch" and "I have just invoiced a customer".
 *
 * ⚠️ **This is NOT the helper text the house rule prohibits.** That rule is
 * about prose restating what a control already says, and it carries a standing
 * exception for a warning about something irreversible — which reads exactly
 * like a tip and is the one kind of sentence that must survive a copy sweep.
 * A Xero push has no undo a CRM delete can reach.
 */

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function AlertIcon() {
  return (
    <svg {...iconProps} className="hidden h-4 w-4 shrink-0 sm:block">
      <path d="M12 4.5 3.2 19.5h17.6L12 4.5Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * Renders nothing on the apex build, so it is safe to mount unconditionally.
 *
 * ⚠️ **Gated on `IS_PREVIEW` — the base path — and deliberately NOT on the
 * branch name.** The question is *where is this deployed*, not *whose code is
 * this*: a preview built from `main` is still pointed at production behind a
 * `/b/` path and still needs the warning, and a local dev run of a feature
 * branch is talking to localhost and does not. `buildInfo.ts` draws that
 * distinction; do not re-derive it here.
 */
export function PreviewBanner() {
  if (!IS_PREVIEW) return null;

  return (
    <div
      role="region"
      aria-label="Preview build"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-danger-border bg-danger-soft px-3 py-1.5 text-xs text-danger sm:gap-x-3 sm:px-6"
    >
      <AlertIcon />
      <p className="m-0 flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5">
        <span className="shrink-0 font-semibold">Preview build</span>
        <span className="truncate font-medium">{BUILD_BRANCH}</span>
        {/*
         * The sha is the only thing distinguishing two builds of one branch, so
         * it is what a bug report has to carry. Hidden below `sm` because the
         * sentence that follows is what actually protects anybody, and it is
         * the half that must survive a narrow screen.
         */}
        {BUILD_COMMIT ? (
          <span className="hidden font-mono text-2xs opacity-80 sm:inline">
            {BUILD_COMMIT}
          </span>
        ) : null}
      </p>
      {/*
       * Stated as a fact about the data rather than as advice. "Be careful" is
       * a tone; "this is the live database" is something a reader can act on.
       */}
      <span className="min-w-0 shrink-0 font-medium">
        Live database — saves, deletes and Xero pushes are real.
      </span>
    </div>
  );
}

/*
 * ⚠️ **DELIBERATELY NOT DISMISSIBLE, AND NOT ROLE-GATED.**
 *
 * Not dismissible: the risk lasts as long as the session does, so a warning
 * that can be cleared is one that is absent for exactly the long working
 * stretch in which somebody forgets which app they are in.
 *
 * Not role-gated: the build switcher that leads here is shown only to a
 * super-admin, which makes it tempting to gate this the same way. That would be
 * backwards. A preview URL is served by nginx to anyone holding it and can be
 * pasted into a message, so the person most likely to arrive on one without
 * knowing what it is, is precisely the person who is not a super-admin. The
 * menu is restricted; the warning is for everyone who ends up here.
 */
